import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Box } from "@mui/material";
import { calculateGrowth } from "components/Reports/data";

const defData = {
	name: "Sales",
	children: [
		{
			name: "Online",
			value: 40,
			children: [
				{ name: "D2C", value: 10 },
				{ name: "MP", value: 30 },
			],
		},
		{
			name: "Offline",
			value: 60,
			children: [
				{ name: "EBO", value: 50 },
				{ name: "MBO", value: 15 },
				{ name: "LFS", value: 35 },
			],
		},
	],
};

// Contrast text color
const getTextColor = (bgColor) => {
	const c = d3.color(bgColor);
	if (!c) return "#fff";
	const luminance = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
	return luminance < 140 ? "#fff" : "#000";
};

const PieSunburst = ({ data = defData, colors, sx = {}, valRoundOffFun, isDiscountType }) => {
	const containerRef = useRef();
	const svgRef = useRef();
	const tooltipRef = useRef();
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;
			setDimensions({ width, height });
		});
		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		const { width, height } = dimensions;
		if (!width || !height || !data) return;

		const radius = Math.min(width, height) / 2;
		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", height);

		const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

		// Tooltip
		const tooltip = d3
			.select("body")
			.append("div")
			.attr("class", "venntooltip")
			.style("position", "absolute")
			// .style("text-align", "center")
			.style("padding", "6px 10px")
			.style("font-size", "var(--font-size-tooltip")
			.style("background", "var(--color-tooltip-background)")
			.style("color", "var(--color-tooltip)")
			.style("border-radius", "6px")
			.style("pointer-events", "none")
			.style("opacity", 0);

		// Parent colors
		const parentNames = data.children.map((d) => d.name);
		const parentColor = Array.isArray(colors)
			? d3.scaleOrdinal().domain(parentNames).range(colors)
			: d3
					.scaleOrdinal()
					.domain(parentNames)
					.range(parentNames.map((name, i) => (colors && colors[name]) || d3.schemeTableau10[i % 10]));

		// ---------------- INNER PIE ----------------
		const innerRadius = 0;
		const innerOuterRadius = radius * 0.6;
		const pieData = data.children.map((d) => ({ name: d.name, value: d.value, ly_value: d.ly_ach_val }));
		const pie = d3.pie().value((d) => d.value)(pieData);
		const arcInner = d3.arc().innerRadius(innerRadius).outerRadius(innerOuterRadius);

		g.selectAll("path.inner-pie")
			.data(pie)
			.join("path")
			.attr("class", "inner-pie")
			.attr("d", arcInner)
			.attr("fill", (d) => parentColor(d.data.name))
			.attr("stroke", "#fff")
			.on("mousemove", (event, d) => {				
				const percent = ((d.data.value / d3.sum(pieData, (x) => x.value)) * 100).toFixed(1);
				const growth = calculateGrowth(d.data.ly_value, d.data.value)
				const growthColor = isDiscountType ? growth > 0 ? "red": "green" : growth > 0 ? "green" : "red";
				tooltip
					.style("opacity", 1)
					.html(`<span>${d.data.name} <b>(${percent}%)</b></span><br/>TY: ${valRoundOffFun(d.data.value)} <span style="color:${growthColor}; font-weight:bold;">(${growth}%)</span><br/>LY: ${valRoundOffFun(d.data.ly_value)}`)
					.style("left", event.pageX + 10 + "px")
					.style("top", event.pageY - 20 + "px");
			})
			.on("mouseout", () => tooltip.style("opacity", 0));

		// Inner labels
		const textGroups = g
			.selectAll("g.inner-text")
			.data(pie)
			.join("g")
			.attr("transform", (d) => `translate(${arcInner.centroid(d)})`)
			.style("cursor", "default");

		// textGroups.each(function (d) {
		// 	const group = d3.select(this);
		// 	const angle = d.endAngle - d.startAngle;
		// 	const percent = ((d.data.value / d3.sum(pieData, (d) => d.value)) * 100).toFixed(0);
		// 	const text = `${d.data.name} ${percent}%`;
		// 	const color = getTextColor(parentColor(d.data.name));

		// 	// Only show text if angle large enough
		// 	if (angle < 0.35) {
		// 		group
		// 			.append("circle")
		// 			.attr("r", 3)
		// 			.attr("fill", color)
		// 			.on("mousemove", (event) => {
		// 				tooltip
		// 					.style("opacity", 1)
		// 					.html(`<b>${d.data.name}</b>: ${d.data.value} (${percent}%)`)
		// 					.style("left", event.pageX + 10 + "px")
		// 					.style("top", event.pageY - 20 + "px");
		// 			})
		// 			.on("mouseout", () => tooltip.style("opacity", 0));
		// 		return;
		// 	}

		// 	group
		// 		.append("text")
		// 		.text(text)
		// 		.attr("fill", color)
		// 		.attr("text-anchor", "middle")
		// 		.attr("alignment-baseline", "middle")
		// 		.style("font-size", "11px")
		// 		.style("font-weight", 600);
		// });

		// ---------------- OUTER RING ----------------
		const gap = radius * 0.1;
		const outerInnerRadius = innerOuterRadius + gap;
		const outerOuterRadius = outerInnerRadius + radius * 0.3;

		const outerArcs = [];
		pie.forEach((p) => {
			const parent = data.children.find((d) => d.name === p.data.name);
			if (!parent.children) return;
			const parentStart = p.startAngle;
			const parentEnd = p.endAngle;
			const total = d3.sum(parent.children, (d) => d.value);
			let currentStart = parentStart;

			// Unique shade palette per parent
			const baseColor = d3.hsl(parentColor(parent.name));
			const childCount = parent.children.length;
			const childShades = Array.from({ length: childCount }, (_, i) => {
				const factor = 1 - i * 0.15;
				const shade = d3.hsl(baseColor.h, baseColor.s, Math.max(0.25, baseColor.l * factor));
				return shade.formatHex();
			});

			parent.children.forEach((child, i) => {
				const angle = (child.value / total) * (parentEnd - parentStart);
				outerArcs.push({
					data: child,
					startAngle: currentStart,
					endAngle: currentStart + angle,
					parent: p.data,
					fill: childShades[i],
				});
				currentStart += angle;
			});
		});

		const arcOuter = d3
			.arc()
			.innerRadius(outerInnerRadius)
			.outerRadius(outerOuterRadius)
			.startAngle((d) => d.startAngle)
			.endAngle((d) => d.endAngle);

		g.selectAll("path.outer-ring")
			.data(outerArcs)
			.join("path")
			.attr("class", "outer-ring")
			.attr("d", arcOuter)
			.attr("fill", (d) => d.fill)
			.attr("stroke", "#fff")
			.on("mousemove", (event, d) => {
				const percent = ((d.data.value / d.parent.value) * 100).toFixed(1);
				const growth = calculateGrowth(d.data.ly_ach_val, d.data.value)
				const growthColor = isDiscountType ? growth > 0 ? "red": "green" : growth > 0 ? "green" : "red";
				tooltip
					.style("opacity", 1)
					.html(`<span>${d.data.name} <b>(${percent}%)</b></span><br/>TY: ${valRoundOffFun(d.data.value)} <span style="color:${growthColor}; font-weight:bold;">(${growth}%)</span><br/>LY: ${valRoundOffFun(d.data.ly_ach_val)}`)
					.style("left", event.pageX + 10 + "px")
					.style("top", event.pageY - 20 + "px");
			})
			.on("mouseout", () => tooltip.style("opacity", 0));

			// ---------------- LEGENDS (TOP-RIGHT) ----------------
			const legendG = svg
				.append("g")
				.attr("class", "legends")
				.attr("transform", `translate(${width - 160}, 20)`); // top-right

			const legendItems = data.children.map((d) => ({
				name: d.name,
				color: parentColor(d.name),
			}));

			legendG
				.selectAll("g.legend-item")
				.data(legendItems)
				.join("g")
				.attr("class", "legend-item")
				.attr("transform", (d, i) => `translate(${i * 80}, 0)`)
				.each(function (d) {
					const g = d3.select(this);

					g.append("rect")
						.attr("width", 12)
						.attr("height", 12)
						.attr("x", 2)
						.attr("y", 15)
						.attr("rx", 3)
						.attr("fill", d.color);

					g.append("text")
						.attr("x", 20)
						.attr("y", 25)
						.attr("fill", "#333")
						.style("font-size", "12px")
						.style("font-weight", 600)
						.text(d.name);
				});


		// Outer labels with overflow detection
		// outerArcs.forEach((d, i) => {
		// 	const pathId = `outerArc${i}`;
		// 	g.append("path")
		// 		.attr("id", pathId)
		// 		.attr(
		// 			"d",
		// 			d3
		// 				.arc()
		// 				.innerRadius((outerInnerRadius + outerOuterRadius) / 2)
		// 				.outerRadius((outerInnerRadius + outerOuterRadius) / 2)
		// 				.startAngle(d.startAngle)
		// 				.endAngle(d.endAngle)
		// 		)
		// 		.attr("fill", "none");

		// 	const percent = ((d.data.value / d.parent.value) * 100).toFixed(0);
		// 	const textStr = `${d.data.name} ${percent}%`;

		// 	const midRadius = (outerInnerRadius + outerOuterRadius) / 2;
		// 	const arcLength = midRadius * (d.endAngle - d.startAngle);

		// 	const tempText = g.append("text").append("textPath").attr("href", `#${pathId}`).text(textStr);
		// 	const textLength = tempText.node().getComputedTextLength();
		// 	tempText.remove();

		// 	if (textLength < arcLength) {
		// 		g.append("text")
		// 			.append("textPath")
		// 			.attr("href", `#${pathId}`)
		// 			.attr("startOffset", "30%")
		// 			.attr("text-anchor", "middle")
		// 			.style("font-size", 10)
		// 			.style("font-weight", 500)
		// 			.style("fill", getTextColor(d.fill))
		// 			.text(textStr);
		// 	} else {
		// 		// transparent hover area for tooltip
		// 		g.append("path")
		// 			.attr(
		// 				"d",
		// 				d3
		// 					.arc()
		// 					.innerRadius(outerInnerRadius)
		// 					.outerRadius(outerOuterRadius)
		// 					.startAngle(d.startAngle)
		// 					.endAngle(d.endAngle)
		// 			)
		// 			.attr("fill", "transparent")
		// 			.on("mousemove", (event) => {
		// 				tooltip
		// 					.style("opacity", 1)
		// 					.html(`<b>${d.data.name}</b>: ${d.data.value} (${percent}%)`)
		// 					.style("left", `${event.offsetX + 10}px`)
		// 					.style("top", `${event.offsetY - 20}px`);
		// 			})
		// 			.on("mouseout", () => tooltip.style("opacity", 0));
		// 	}
		// });
		return () => {
    tooltip.remove();
  };
	}, [data, colors, dimensions]);

	return (
		<Box ref={containerRef} sx={{ width: "100%", height: "100%", position: "relative", ...sx }}>
			<svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
			<div ref={tooltipRef} />
		</Box>
	);
};

export default PieSunburst;
